import update from 'immutability-helper';
import PropTypes from 'prop-types';
import React from 'react';
import isString from '../isString.js';
import {
  getPassthroughProps
} from '../componentHelpers.js';
import PromiseDelayer from '../PromiseDelayer.js';
import StalePromiseFilter from '../StalePromiseFilter.js';

let nextId = 0;

const withValidation = (WrappedComponent) => {
  class ValidatedComponent extends React.Component {
    constructor(props, context) {
      super(props, context);

      if (!context.form) {
        throw new Error('FormField must be inside a <Form/> component');
      }

      this.state = {
        hasSubmitted: context.form.hasSubmitted,
        loading: context.form.loading,
        validationMessage: '',
        validationProgress: '',
        validationState: 'success',
      };

      this._id = nextId++;

      this._staleValidationResultFilter = new StalePromiseFilter();
      this._validationDelayer = new PromiseDelayer(this.props.validationDelayMilliseconds);

      this._unregisterContext = context.form.onStateChange(this.handleContextChange);
    }

    handleContextChange = (context) => {
      if (context.hasSubmitted !== this.state.hasSubmitted) {
        this.setState({ hasSubmitted: context.hasSubmitted });
      }

      if (context.loading !== this.state.loading) {
        this.setState({ loading: context.loading });
      }
    }

    componentDidMount() {
      this.validate();
    }

    validate() {
      if (this.state.loading) {
        return;
      }

      if (this.props.isRequired && (this.props.value === '' || this.props.value === null)) {
        this.setValidationStatus('validated', this.isRequiredErrorMessage);
        this._validationDelayer.cancel();
        this._staleValidationResultFilter.cancel();
        return;
      }

      if (this.props.validate.length === 0) {
        this.setValidationStatus('validated');
        this._validationDelayer.cancel();
        this._staleValidationResultFilter.cancel();
        return;
      }

      this.setValidationStatus('');

      this._promise = this._validationDelayer.run(() =>
        this._staleValidationResultFilter.run(() => {
          this.setValidationStatus('validating');
          return this.executeValidators();
        })
      )
        .then(result => {
          this.setValidationStatus('validated', result);
        }, x => {
          if (!this._staleValidationResultFilter.ownsRejectionValue(x) && !this._validationDelayer.ownsRejectionValue(x)) {
            console.error('Validator threw error', x);
          }
        });
    }

    setValidationStatus(status, validationResult) {
      validationResult = translateValidationResult(validationResult);

      this.context.form.setField(
        this._id,
        status === 'validated'
          ? validationResult.state !== 'error'
          : null
      );

      this.setState({
        validationMessage: validationResult.message,
        validationProgress: status,
        validationState: validationResult.state,
      });

      if (status === 'validated') {
        this.props.onValidated(validationResult.state);
      }
    }

    get isRequiredErrorMessage() {
      let label = isString(this.props.label) ? this.props.label : 'This field';
      return this.props.isRequiredErrorMessage || `${label} is required.`;
    }

    executeValidators() {
      let { label, value } = this.props;

      if (value === null) {
        value = '';
      }

      const defaultSuccessResult = translateValidationResult();

      const validate = (validators = [], successResult=defaultSuccessResult) => {
        if (validators.length === 0) {
          return Promise.resolve(successResult);
        }

        let validator = normalizeValidator(validators[0]);

        this.setState({ validationMessage: validator.message });

        return Promise.resolve(validator.validate(value, { label }))
          .then(result => {
            result = translateValidationResult(result);
            if (result.state === 'error') {
              return result;
            }

            if (result.priority > successResult.priority) {
              successResult = result;
            }

            return validate(validators.slice(1), successResult);
          });
      };

      return validate(this.props.validate);
    }

    render() {
      let { hasSubmitted, validationMessage, validationProgress, validationState } = this.state;

      return (
        <WrappedComponent
          hasSubmitted={hasSubmitted}
          isValidating={validationProgress === 'validating'}
          isValidated={validationProgress === 'validated'}
          label={this.props.label}
          validationMessage={validationMessage}
          validationState={validationState}
          value={this.props.value}
          {...getPassthroughProps(this)}
        />
      );
    }

    componentWillUnmount() {
      this._unregisterContext();
      this.context.form.removeField(this._id);
    }

    componentDidUpdate(prevProps, prevState) {
      if (!areSame(prevProps.value, this.props.value) ||
          prevProps.isRequired !== this.props.isRequired ||
          prevProps.validate.length !== this.props.validate.length ||
          prevState.loading !== this.state.loading) {
        this.validate();
      }
    }
  }

  ValidatedComponent.propTypes = {
    isRequired: PropTypes.bool,
    isRequiredErrorMessage: PropTypes.string,
    label: PropTypes.node,
    onValidated: PropTypes.func,
    validate: PropTypes.array,
    validationDelayMilliseconds: PropTypes.number,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
  };

  ValidatedComponent.defaultProps = {
    isRequired: false,
    onValidated: () => { },
    validate: [],
    validationDelayMilliseconds: 500,
  };

  ValidatedComponent.contextTypes = {
    form: PropTypes.object,
  };

  return ValidatedComponent;
};

const normalizeValidator = (validator) => {
  return typeof validator === 'function'
    ? { message: '', validate: validator }
    : { message: '', validate: () => { }, ...validator };
};

export { normalizeValidator };

const areSame = (left, right) => {
  return left === right || Object.is(left, right);
};

const translateValidationResult = (result) => {
  if (result && (isString(result) || React.isValidElement(result))) {
    result = {
      state: 'error',
      message: result,
    };
  }

  return {
    state: 'success',
    message: '',
    priority: getPriority(result),
    ...result,
  };
};

const getPriority = (result) => {
  let { state } = result || {};

  switch (state) {
  case 'error':
    return 99999;
  case 'info':
    return 20;
  case 'success':
    return 10;
  default:
    return 0;
  }
};

export default withValidation;
